import React, { useEffect, useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ChevronRight, Check, ArrowRight, Upload, Info, X, FileText, HelpCircle, QrCode, Tag, Barcode, Pencil, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DataSheetModal } from '../components/DataSheetModal';
// --- TYPES ---
type AssetTypeOption = 'asset-barcode' | 'property-of' | 'asset-qr' | 'create-own' | null;
type MaterialOption = 'pvc' | 'polyester' | 'mark-resist' | null;
type ColourOption = 'white' | 'silver' | null;
type FinishOption = 'no-laminate' | 'gloss' | 'matt' | null;
type SizeOption = '60x40' | '50x25' | '40x20' | null;
type QuantityOption = 100 | 200 | 300 | 400 | 500 | 600 | null;
interface DesignVariant {
  id: string;
  label: string;
  image: string;
  size?: SizeOption;
}
interface AssetType {
  id: AssetTypeOption;
  label: string;
  desc: string;
  designs: DesignVariant[];
}
// --- DATA (Copied from AssetLabelsPage to ensure independence) ---
const assetTypes: AssetType[] = [{
  id: 'asset-barcode',
  label: 'Asset Barcode',
  desc: 'Standard barcode identification label',
  designs: [{
    id: 'barcode-60x40',
    label: 'Barcode 60x40',
    size: '60x40',
    image: 'https://cdn.magicpatterns.com/uploads/rCPQBtAEN3o8prh4urA2um/asset-barcode-60x40-design-option-1.png'
  }, {
    id: 'barcode-50x25',
    label: 'Barcode 50x25',
    size: '50x25',
    image: 'https://cdn.magicpatterns.com/uploads/jbiTWgRxmSchavzXrEYhZA/asset-barcode-50x25-design-option-1.png'
  }, {
    id: 'barcode-40x20',
    label: 'Barcode 40x20',
    size: '40x20',
    image: 'https://cdn.magicpatterns.com/uploads/rDdVguduk1sTeqdwxt9qE9/asset-barcode-40x20-design-option-1.png'
  }]
}, {
  id: 'property-of',
  label: 'Property Of',
  desc: 'Ownership identification label',
  designs: [{
    id: 'property-1',
    label: 'Property Of Style 1',
    image: 'https://cdn.magicpatterns.com/uploads/aZBEnmxfu2HAJ6tgPZ7rwT/Screenshot_2026-02-20_at_13.45.59.png'
  }]
}, {
  id: 'asset-qr',
  label: 'Asset QR',
  desc: 'QR code for digital asset tracking',
  designs: [{
    id: 'qr-1',
    label: 'QR Style 1',
    image: 'https://cdn.magicpatterns.com/uploads/d9oTNWiR2aVzqB1nGvdrUs/Screenshot_2026-02-20_at_13.46.03.png'
  }]
}, {
  id: 'create-own',
  label: 'Create Your Own',
  desc: 'Upload your own artwork or design',
  designs: []
}];
const materials = [{
  id: 'pvc' as const,
  label: 'PVC',
  desc: 'Cost-effective, versatile general-purpose PVC'
}, {
  id: 'polyester' as const,
  label: 'Polyester',
  desc: 'Durable polyester with excellent chemical and heat resistance'
}, {
  id: 'mark-resist' as const,
  label: 'Mark Resist',
  desc: 'Anti-graffiti PVC that resists pen and marker damage'
}];
const colours = [{
  id: 'white' as const,
  label: 'White',
  desc: 'Clean white polyester for clear print visibility'
}, {
  id: 'silver' as const,
  label: 'Silver',
  desc: 'Premium metallic finish for professional identification'
}];
const finishes = [{
  id: 'no-laminate' as const,
  label: 'No laminate'
}, {
  id: 'gloss' as const,
  label: 'Gloss laminated'
}, {
  id: 'matt' as const,
  label: 'Matt laminated'
}];
const sizes = [{
  id: '60x40' as const,
  label: '60 × 40 mm'
}, {
  id: '50x25' as const,
  label: '50 × 25 mm'
}, {
  id: '40x20' as const,
  label: '40 × 20 mm'
}];
const quantities: QuantityOption[] = [100, 200, 300, 400, 500, 600];
// --- PRICING DATA ---
const PRICING_MATRIX: Record<string, number[]> = {
  'pvc-white-no-laminate-60x40': [75.0, 44.0, 30.67, 26.0, 22.4, 21.67],
  'pvc-white-no-laminate-50x25': [75.0, 40.5, 28.0, 26.0, 22.4, 20.0],
  'pvc-white-no-laminate-40x20': [71.0, 39.0, 28.33, 23.0, 21.6, 19.33],
  'pvc-white-gloss-60x40': [82.0, 48.0, 33.67, 28.5, 24.6, 23.83],
  'pvc-white-gloss-50x25': [82.0, 44.5, 30.67, 28.5, 24.6, 22.0],
  'pvc-white-gloss-40x20': [78.0, 43.0, 31.17, 25.3, 23.8, 21.33],
  'pvc-white-matt-60x40': [82.0, 48.0, 33.67, 28.5, 24.6, 23.83],
  'pvc-white-matt-50x25': [82.0, 44.5, 30.67, 28.5, 24.6, 22.0],
  'pvc-white-matt-40x20': [78.0, 43.0, 31.17, 25.3, 23.8, 21.33],
  'polyester-white-gloss-60x40': [85.0, 50.0, 35.0, 29.5, 25.4, 24.5],
  'polyester-white-gloss-50x25': [85.0, 46.0, 32.0, 29.5, 25.4, 23.0],
  'polyester-white-gloss-40x20': [81.0, 44.5, 32.33, 26.5, 24.6, 22.0],
  'polyester-white-matt-60x40': [85.0, 50.0, 35.0, 29.5, 25.4, 24.5],
  'polyester-white-matt-50x25': [85.0, 46.0, 32.0, 29.5, 25.4, 23.0],
  'polyester-white-matt-40x20': [81.0, 44.5, 32.33, 26.5, 24.6, 22.0],
  'polyester-silver-gloss-60x40': [88.0, 52.0, 36.33, 30.5, 26.2, 25.33],
  'polyester-silver-gloss-50x25': [88.0, 47.5, 33.0, 30.5, 26.2, 23.83],
  'polyester-silver-gloss-40x20': [84.0, 46.0, 33.5, 27.5, 25.4, 22.83],
  'polyester-silver-matt-60x40': [88.0, 52.0, 36.33, 30.5, 26.2, 25.33],
  'polyester-silver-matt-50x25': [88.0, 47.5, 33.0, 30.5, 26.2, 23.83],
  'polyester-silver-matt-40x20': [84.0, 46.0, 33.5, 27.5, 25.4, 22.83],
  'mark-resist-no-laminate-60x40': [92.0, 54.0, 37.67, 31.5, 27.0, 26.17],
  'mark-resist-no-laminate-50x25': [92.0, 49.5, 34.33, 31.5, 27.0, 24.67],
  'mark-resist-no-laminate-40x20': [88.0, 48.0, 34.83, 28.5, 26.2, 23.67]
};
const SKU_MATRIX: Record<string, string> = {
  'pvc-white-no-laminate-60x40': '41138',
  'pvc-white-no-laminate-50x25': '41139',
  'pvc-white-no-laminate-40x20': '41140',
  'pvc-white-gloss-60x40': '41141',
  'pvc-white-gloss-50x25': '41142',
  'pvc-white-gloss-40x20': '41143',
  'pvc-white-matt-60x40': '41144',
  'pvc-white-matt-50x25': '41145',
  'pvc-white-matt-40x20': '41146',
  'polyester-white-gloss-60x40': '41147',
  'polyester-white-gloss-50x25': '41148',
  'polyester-white-gloss-40x20': '41149',
  'polyester-white-matt-60x40': '41150',
  'polyester-white-matt-50x25': '41151',
  'polyester-white-matt-40x20': '41152',
  'polyester-silver-gloss-60x40': '41153',
  'polyester-silver-gloss-50x25': '41154',
  'polyester-silver-gloss-40x20': '41155',
  'polyester-silver-matt-60x40': '41156',
  'polyester-silver-matt-50x25': '41157',
  'polyester-silver-matt-40x20': '41158',
  'mark-resist-no-laminate-60x40': '41159',
  'mark-resist-no-laminate-50x25': '41160',
  'mark-resist-no-laminate-40x20': '41161'
};
const VARIATION_DESCRIPTIONS: Record<string, string> = {
  'pvc-white-no-laminate': 'DuraLabel™ PVC no laminate',
  'pvc-white-gloss': 'DuraLabel™ PVC + gloss laminated',
  'pvc-white-matt': 'DuraLabel™ PVC + matt laminated',
  'polyester-white-gloss': 'DuraLabel™ Polyester white gloss laminated',
  'polyester-white-matt': 'DuraLabel™ Polyester white matt laminated',
  'polyester-silver-gloss': 'DuraLabel™ Polyester silver gloss laminated',
  'polyester-silver-matt': 'DuraLabel™ Polyester silver matt laminated',
  'mark-resist-no-laminate': 'DuraLabel™ Mark Resist PVC'
};
const WHATS_INCLUDED: Record<string, string[]> = {
  pvc: ['Durable white PVC', 'Permanent adhesive', 'Indoor / light industrial use', 'UV stable digital print', 'Custom artwork supported', 'Free UK delivery'],
  polyester: ['Industrial polyester face stock', 'Enhanced durability', 'Gloss laminated or matt laminated protective surface', 'Chemical and heat resistant', 'UV stable digital print', 'Free UK delivery'],
  'mark-resist': ['Anti-graffiti surface', 'Resistant to solvent markers', 'Industrial-grade adhesive', 'UV stable digital print', 'Custom artwork supported', 'Free UK delivery'],
  default: ['Durable construction', 'Multiple size options', 'Weather resistant', 'UV stable printing', 'Easy application', 'Free UK delivery']
};
const MATERIAL_SUMMARIES: Record<string, string> = {
  pvc: 'Cost-effective white PVC asset labels ideal for indoor asset tracking, equipment identification, and general-purpose labelling. Suitable for office furniture, IT equipment, and warehouse inventory.',
  'polyester-white': 'Premium white polyester asset labels engineered for demanding environments. Excellent resistance to chemicals, heat, and UV exposure. Ideal for manufacturing equipment, outdoor assets, and laboratory instruments.',
  'polyester-silver': 'Professional silver polyester asset labels with a metallic finish for high-visibility asset identification. Superior durability in harsh industrial conditions. Perfect for machinery, tools, and high-value equipment.',
  'mark-resist': 'Specialist mark-resistant PVC labels that prevent tampering via pen or pencil marking. Designed for secure asset tracking in environments where label integrity is critical.',
  default: 'Durable, custom printed asset labels suitable for industrial, commercial and equipment identification. Select your material to see detailed specifications.'
};
const MATERIAL_SPECS: Record<string, Array<{
  label: string;
  value: string;
}>> = {
  pvc: [{
    label: 'Material',
    value: 'White PVC'
  }, {
    label: 'Thickness',
    value: '100 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-20°C to +80°C'
  }, {
    label: 'Application',
    value: 'Indoor/sheltered outdoor'
  }],
  'polyester-white': [{
    label: 'Material',
    value: 'White Polyester'
  }, {
    label: 'Thickness',
    value: '50 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-40°C to +150°C'
  }, {
    label: 'Application',
    value: 'Indoor/outdoor, chemical environments'
  }],
  'polyester-silver': [{
    label: 'Material',
    value: 'Silver Polyester'
  }, {
    label: 'Thickness',
    value: '50 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-40°C to +150°C'
  }, {
    label: 'Application',
    value: 'Indoor/outdoor, industrial environments'
  }],
  'mark-resist': [{
    label: 'Material',
    value: 'Mark Resist PVC'
  }, {
    label: 'Thickness',
    value: '120 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-20°C to +80°C'
  }, {
    label: 'Application',
    value: 'Secure environments, tamper-evident'
  }]
};
// --- MODAL COMPONENT ---
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-1385">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col" data-id="element-1386">
        <div className="flex items-center justify-between p-6 border-b border-slate-100" data-id="element-1387">
          <h3 className="text-xl font-bold text-[#0A1628]" data-id="element-1388">{title}</h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1389">
            <X className="h-5 w-5" data-id="element-1390" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto" data-id="element-1391">{children}</div>
      </motion.div>
    </div>;
}
// --- MAIN COMPONENT ---
export function SimplifiedPage() {
  // State
  const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeOption>(null);
  const [selectedDesign, setSelectedDesign] = useState<DesignVariant | null>(null);
  const [selectedSize, setSelectedSize] = useState<SizeOption>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialOption>(null);
  const [selectedColour, setSelectedColour] = useState<ColourOption>(null);
  const [selectedFinish, setSelectedFinish] = useState<FinishOption>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<QuantityOption>(100);
  // Modals
  const [isDataSheetOpen, setIsDataSheetOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isMaterialGuideOpen, setIsMaterialGuideOpen] = useState(false);
  // Derived State
  const internalMaterialKey = useMemo(() => {
    if (!selectedMaterial) return null;
    if (selectedMaterial === 'pvc') return 'pvc-white';
    if (selectedMaterial === 'polyester' && selectedColour) return `polyester-${selectedColour}`;
    if (selectedMaterial === 'mark-resist') return 'mark-resist';
    return null;
  }, [selectedMaterial, selectedColour]);
  const resolvedFinish = useMemo(() => {
    if (selectedMaterial === 'mark-resist') return 'no-laminate';
    return selectedFinish;
  }, [selectedMaterial, selectedFinish]);
  const availableFinishes = useMemo(() => {
    if (selectedMaterial === 'pvc') return ['no-laminate', 'gloss', 'matt'];
    if (selectedMaterial === 'polyester' && selectedColour) return ['gloss', 'matt'];
    if (selectedMaterial === 'mark-resist') return [];
    return [];
  }, [selectedMaterial, selectedColour]);
  // Effects to reset invalid selections
  useEffect(() => {
    if (selectedMaterial === 'mark-resist') {
      setSelectedFinish('no-laminate');
    }
  }, [selectedMaterial]);
  useEffect(() => {
    if (selectedMaterial !== 'polyester') {
      setSelectedColour(null);
    }
  }, [selectedMaterial]);
  useEffect(() => {
    if (selectedMaterial && selectedMaterial !== 'mark-resist' && selectedFinish && !availableFinishes.includes(selectedFinish)) {
      setSelectedFinish(null);
    }
  }, [selectedMaterial, selectedColour, availableFinishes, selectedFinish]);
  // SKU & Pricing
  const skuKey = useMemo(() => {
    if (!internalMaterialKey || !resolvedFinish || !selectedSize) return null;
    return `${internalMaterialKey}-${resolvedFinish}-${selectedSize}`;
  }, [internalMaterialKey, resolvedFinish, selectedSize]);
  const pricingData = useMemo(() => {
    if (!skuKey) return null;
    const sku = SKU_MATRIX[skuKey];
    if (!sku) return null;
    const prices = PRICING_MATRIX[skuKey];
    if (!prices) return null;
    const qty = selectedQuantity || 100;
    const quantityIndex = quantities.indexOf(qty);
    if (quantityIndex === -1) return null;
    const pricePer100 = prices[quantityIndex];
    const totalPrice = (pricePer100 * (qty / 100)).toFixed(2);
    return {
      pricePer100: pricePer100.toFixed(2),
      totalPrice,
      sku
    };
  }, [skuKey, selectedQuantity]);
  // Content Data
  const includesItems = useMemo(() => {
    if (!selectedMaterial) return WHATS_INCLUDED['default'];
    return WHATS_INCLUDED[selectedMaterial] || WHATS_INCLUDED['default'];
  }, [selectedMaterial]);
  const specsKey = useMemo(() => {
    if (!selectedMaterial) return null;
    if (selectedMaterial === 'pvc') return 'pvc';
    if (selectedMaterial === 'polyester' && selectedColour) return `polyester-${selectedColour}`;
    if (selectedMaterial === 'mark-resist') return 'mark-resist';
    return selectedMaterial;
  }, [selectedMaterial, selectedColour]);
  const productSummary = specsKey ? MATERIAL_SUMMARIES[specsKey] || MATERIAL_SUMMARIES['default'] : MATERIAL_SUMMARIES['default'];
  const materialDisplayLabel = useMemo(() => {
    if (!selectedMaterial) return '';
    const mat = materials.find(m => m.id === selectedMaterial);
    const label = mat?.label || '';
    if (selectedMaterial === 'polyester' && selectedColour) {
      return `${label} ${selectedColour.charAt(0).toUpperCase() + selectedColour.slice(1)}`;
    }
    return label;
  }, [selectedMaterial, selectedColour]);
  // Handlers
  const handleLabelStyleSelect = (typeId: AssetTypeOption, design: DesignVariant | null) => {
    setSelectedAssetType(typeId);
    setSelectedDesign(design);
  };
  const handleMaterialSelect = (matId: MaterialOption) => {
    setSelectedMaterial(matId);
    setSelectedColour(null);
    setSelectedFinish(null);
  };
  const handleColourSelect = (colId: ColourOption) => {
    setSelectedColour(colId);
    setSelectedFinish(null);
  };
  const isConfigComplete = internalMaterialKey && resolvedFinish && selectedSize && selectedQuantity && selectedAssetType;
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-1392">
      <Header data-id="element-1393" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-1394">
        {/* Breadcrumb */}
        <div className="border-b border-slate-100" data-id="element-1395">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-3" data-id="element-1396">
            <nav className="flex items-center gap-2 text-sm" data-id="element-1397">
              <a href="/" className="text-slate-400 hover:text-[#0052CC] transition-colors" data-id="element-1398">
                Home
              </a>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" data-id="element-1399" />
              <a href="/products" className="text-slate-400 hover:text-[#0052CC] transition-colors" data-id="element-1400">
                Identification Labels
              </a>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" data-id="element-1401" />
              <span className="text-[#0A1628]" data-id="element-1402">Asset labels (simplified)</span>
            </nav>
          </div>
        </div>

        <section className="py-12 md:py-16" data-id="element-1403">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1404">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20" data-id="element-1405">
              {/* Left Column: Builder */}
              <div className="lg:col-span-7 space-y-12" data-id="element-1406">
                <div className="mb-8" data-id="element-1407">
                  <h1 className="text-4xl md:text-5xl font-extrabold text-[#0A1628] tracking-tight leading-[1.08] mb-2" data-id="element-1408">
                    Asset labels
                  </h1>
                  <p className="text-lg text-slate-500" data-id="element-1409">Brand: DuraLabel™</p>
                </div>

                {/* STEP 1: Label Style */}
                <div data-id="element-1410">
                  <div className="flex items-center gap-3 mb-6" data-id="element-1411">
                    <span className="h-8 w-8 rounded-full bg-[#0A1628] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" data-id="element-1412">
                      1
                    </span>
                    <h2 className="text-xl font-bold text-[#0A1628]" data-id="element-1413">
                      Choose label style
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1414">
                    {assetTypes.map(type => {
                    // If type has designs, render them. If not (Create Own), render as single option.
                    if (type.designs.length > 0) {
                      return type.designs.map(design => {
                        const isSelected = selectedAssetType === type.id && selectedDesign?.id === design.id;
                        return <motion.button key={`${type.id}-${design.id}`} onClick={() => handleLabelStyleSelect(type.id, design)} whileTap={{
                          scale: 0.98
                        }} className={`p-4 rounded-xl border-2 text-left transition-all flex items-start gap-4 ${isSelected ? 'border-[#0052CC] bg-[#0052CC]/5 ring-1 ring-[#0052CC]' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-1415">
                              <div className="w-20 h-14 bg-white border border-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-1416">
                                <img src={design.image} alt={design.label} className="w-full h-full object-contain p-1" data-id="element-1417" />
                              </div>
                              <div data-id="element-1418">
                                <span className="block font-bold text-[#0A1628] text-sm mb-1" data-id="element-1419">
                                  {type.label}
                                </span>
                                <span className="block text-xs text-slate-500" data-id="element-1420">
                                  {design.label}
                                </span>
                              </div>
                              {isSelected && <div className="ml-auto bg-[#0052CC] text-white rounded-full p-1" data-id="element-1421">
                                  <Check className="h-3 w-3" data-id="element-1422" />
                                </div>}
                            </motion.button>;
                      });
                    } else {
                      // Create Your Own
                      const isSelected = selectedAssetType === type.id;
                      return <motion.button key={type.id} onClick={() => handleLabelStyleSelect(type.id, null)} whileTap={{
                        scale: 0.98
                      }} className={`p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${isSelected ? 'border-[#0052CC] bg-[#0052CC]/5 ring-1 ring-[#0052CC]' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-1423">
                            <div className="w-20 h-14 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 text-slate-400" data-id="element-1424">
                              <Pencil className="h-6 w-6" data-id="element-1425" />
                            </div>
                            <div data-id="element-1426">
                              <span className="block font-bold text-[#0A1628] text-sm mb-1" data-id="element-1427">
                                {type.label}
                              </span>
                              <span className="block text-xs text-slate-500" data-id="element-1428">
                                {type.desc}
                              </span>
                            </div>
                            {isSelected && <div className="ml-auto bg-[#0052CC] text-white rounded-full p-1" data-id="element-1429">
                                <Check className="h-3 w-3" data-id="element-1430" />
                              </div>}
                          </motion.button>;
                    }
                  })}
                  </div>
                </div>

                {/* STEP 2: Size & Material */}
                <div className={`transition-opacity duration-300 ${selectedAssetType ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-1431">
                  <div className="flex items-center gap-3 mb-6" data-id="element-1432">
                    <span className="h-8 w-8 rounded-full bg-[#0A1628] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" data-id="element-1433">
                      2
                    </span>
                    <h2 className="text-xl font-bold text-[#0A1628]" data-id="element-1434">
                      Size & material
                    </h2>
                  </div>

                  <div className="space-y-8 pl-11" data-id="element-1435">
                    {/* Size Selector */}
                    <div data-id="element-1436">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-1437">
                        Select size
                      </h3>
                      <div className="flex flex-wrap gap-3" data-id="element-1438">
                        {sizes.map(size => <button key={size.id} onClick={() => setSelectedSize(size.id)} className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${selectedSize === size.id ? 'bg-[#0A1628] text-white border-[#0A1628]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`} data-id="element-1439">
                            {size.label}
                          </button>)}
                      </div>
                    </div>

                    {/* Material Selector */}
                    <div data-id="element-1440">
                      <div className="flex items-center justify-between mb-3" data-id="element-1441">
                        <h3 className="text-sm font-semibold text-slate-900" data-id="element-1442">
                          Select material
                        </h3>
                        <button onClick={() => setIsMaterialGuideOpen(true)} className="text-xs font-medium text-[#0052CC] hover:underline flex items-center gap-1" data-id="element-1443">
                          <HelpCircle className="h-3 w-3" data-id="element-1444" /> Guide
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1445">
                        {materials.map(material => <button key={material.id} onClick={() => handleMaterialSelect(material.id)} className={`p-3 rounded-lg border text-left transition-all ${selectedMaterial === material.id ? 'border-[#0052CC] bg-[#0052CC]/5 ring-1 ring-[#0052CC]' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-1446">
                            <span className="block text-sm font-bold text-[#0A1628]" data-id="element-1447">
                              {material.label}
                            </span>
                            <span className="block text-[11px] text-slate-500 mt-1 leading-tight" data-id="element-1448">
                              {material.desc}
                            </span>
                          </button>)}
                      </div>
                    </div>

                    {/* Colour (Conditional) */}
                    {selectedMaterial === 'polyester' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} data-id="element-1449">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-1450">
                          Select colour
                        </h3>
                        <div className="flex gap-3" data-id="element-1451">
                          {colours.map(colour => <button key={colour.id} onClick={() => handleColourSelect(colour.id)} className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${selectedColour === colour.id ? 'bg-[#0A1628] text-white border-[#0A1628]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`} data-id="element-1452">
                              {colour.label}
                            </button>)}
                        </div>
                      </motion.div>}

                    {/* Finish (Conditional) */}
                    {selectedMaterial && selectedMaterial !== 'mark-resist' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} data-id="element-1453">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-1454">
                          Select finish
                        </h3>
                        <div className="flex flex-wrap gap-3" data-id="element-1455">
                          {finishes.filter(f => availableFinishes.includes(f.id)).map(finish => <button key={finish.id} onClick={() => setSelectedFinish(finish.id as FinishOption)} className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${selectedFinish === finish.id ? 'bg-[#0A1628] text-white border-[#0A1628]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`} data-id="element-1456">
                                {finish.label}
                              </button>)}
                        </div>
                      </motion.div>}
                  </div>
                </div>

                {/* STEP 3: Quantity */}
                <div className={`transition-opacity duration-300 ${selectedSize && selectedMaterial && (selectedMaterial === 'mark-resist' || selectedFinish && (selectedMaterial !== 'polyester' || selectedColour)) ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-1457">
                  <div className="flex items-center gap-3 mb-6" data-id="element-1458">
                    <span className="h-8 w-8 rounded-full bg-[#0A1628] text-white text-sm font-bold flex items-center justify-center flex-shrink-0" data-id="element-1459">
                      3
                    </span>
                    <h2 className="text-xl font-bold text-[#0A1628]" data-id="element-1460">
                      Quantity
                    </h2>
                  </div>

                  <div className="pl-11" data-id="element-1461">
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4" data-id="element-1462">
                      {quantities.map(qty => <button key={qty} onClick={() => setSelectedQuantity(qty)} className={`py-3 px-1 text-center rounded-lg border text-sm font-medium transition-all ${selectedQuantity === qty ? 'bg-[#0A1628] text-white border-[#0A1628]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`} data-id="element-1463">
                          {qty}
                        </button>)}
                    </div>
                    <button onClick={() => setIsBulkModalOpen(true)} className="text-sm text-[#0052CC] font-medium hover:underline" data-id="element-1464">
                      Need more? Get bulk pricing
                    </button>
                  </div>
                </div>

                {/* Dynamic "Key Features" */}
                {selectedMaterial && <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 mt-8" data-id="element-1465">
                    <h3 className="font-bold text-[#0A1628] mb-4" data-id="element-1466">
                      Key features –{' '}
                      {materials.find(m => m.id === selectedMaterial)?.label}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1467">
                      {includesItems.map((item, i) => <div key={i} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-1468">
                          <Check className="h-4 w-4 text-[#0052CC] flex-shrink-0" data-id="element-1469" />
                          {item}
                        </div>)}
                    </div>
                  </div>}
              </div>

              {/* Right Column: Summary & Pricing (Sticky) */}
              <div className="lg:col-span-5" data-id="element-1470">
                <div className="sticky top-[160px] bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden" data-id="element-1471">
                  <div className="p-6 bg-slate-50 border-b border-slate-200" data-id="element-1472">
                    <h3 className="font-bold text-[#0A1628]" data-id="element-1473">Order summary</h3>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-1474">
                    {/* Summary List */}
                    <div className="space-y-3 text-sm" data-id="element-1475">
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-1476">
                        <span className="text-slate-500" data-id="element-1477">Label style</span>
                        <span className="font-medium text-right text-[#0A1628]" data-id="element-1478">
                          {selectedAssetType ? assetTypes.find(t => t.id === selectedAssetType)?.label : '-'}
                          {selectedDesign && <span className="block text-xs text-slate-400 font-normal" data-id="element-1479">
                              {selectedDesign.label}
                            </span>}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-1480">
                        <span className="text-slate-500" data-id="element-1481">Size</span>
                        <span className="font-medium text-[#0A1628]" data-id="element-1482">
                          {selectedSize ? sizes.find(s => s.id === selectedSize)?.label : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-1483">
                        <span className="text-slate-500" data-id="element-1484">Material</span>
                        <span className="font-medium text-[#0A1628]" data-id="element-1485">
                          {selectedMaterial ? materials.find(m => m.id === selectedMaterial)?.label : '-'}
                        </span>
                      </div>
                      {selectedMaterial === 'polyester' && <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-1486">
                          <span className="text-slate-500" data-id="element-1487">Colour</span>
                          <span className="font-medium text-[#0A1628]" data-id="element-1488">
                            {selectedColour ? colours.find(c => c.id === selectedColour)?.label : '-'}
                          </span>
                        </div>}
                      {selectedMaterial && selectedMaterial !== 'mark-resist' && <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-1489">
                            <span className="text-slate-500" data-id="element-1490">Finish</span>
                            <span className="font-medium text-[#0A1628]" data-id="element-1491">
                              {resolvedFinish ? finishes.find(f => f.id === resolvedFinish)?.label : '-'}
                            </span>
                          </div>}
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-1492">
                        <span className="text-slate-500" data-id="element-1493">Quantity</span>
                        <span className="font-medium text-[#0A1628]" data-id="element-1494">
                          {selectedQuantity}
                        </span>
                      </div>
                    </div>

                    {/* Pricing Display */}
                    <div className="pt-4" data-id="element-1495">
                      {pricingData && pricingData.sku && <div className="text-xs font-mono text-slate-400 mb-1" data-id="element-1496">
                          SKU: {pricingData.sku}
                        </div>}

                      <div className="flex items-end justify-between mb-2" data-id="element-1497">
                        <span className="text-slate-500" data-id="element-1498">
                          Total Price (ex. VAT)
                        </span>
                        <span className="text-3xl font-extrabold text-[#0A1628]" data-id="element-1499">
                          {pricingData ? `£${pricingData.totalPrice}` : '£0.00'}
                        </span>
                      </div>

                      {pricingData && <p className="text-right text-xs text-green-600 font-medium mb-6" data-id="element-1500">
                          £{pricingData.pricePer100} per 100 labels
                        </p>}

                      <button disabled={!isConfigComplete} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${isConfigComplete ? 'bg-[#0052CC] text-white hover:bg-[#0040A0] shadow-lg shadow-[#0052CC]/20' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-1501">
                        Add to Basket
                      </button>
                    </div>

                    {/* Technical Sheet Button */}
                    {selectedMaterial && <button onClick={() => setIsDataSheetOpen(true)} className="w-full flex items-center justify-center gap-2 py-3 border border-slate-200 rounded-xl text-sm font-semibold text-[#0A1628] hover:bg-slate-50 transition-colors" data-id="element-1502">
                        <FileText className="h-4 w-4" data-id="element-1503" />
                        View Technical Sheet
                      </button>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1504" />

      {/* Modals */}
      <DataSheetModal isOpen={isDataSheetOpen} onClose={() => setIsDataSheetOpen(false)} imageUrl="https://cdn.magicpatterns.com/uploads/9azdKatRqabVvwJLb3Kpam/Screenshot_2026-02-05_at_01.27.39.png" title={`DuraLabel™ ${materialDisplayLabel || 'Asset Label'} - Technical Data Sheet`} data-id="element-1505" />

      <Modal isOpen={isMaterialGuideOpen} onClose={() => setIsMaterialGuideOpen(false)} title="Material guide" data-id="element-1506">
        <div className="space-y-6" data-id="element-1507">
          {materials.map(m => <div key={m.id} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0" data-id="element-1508">
              <h4 className="font-bold text-[#0A1628] mb-1" data-id="element-1509">{m.label}</h4>
              <p className="text-sm text-slate-600" data-id="element-1510">{m.desc}</p>
              {m.id === 'polyester' && <p className="text-xs text-slate-400 mt-1" data-id="element-1511">
                  Available in White and Silver
                </p>}
            </div>)}
          <div className="pt-4 mt-4 border-t border-slate-100" data-id="element-1512">
            <a href="/contact" className="w-full flex items-center justify-center px-6 py-3 bg-[#0A1628] text-white font-bold rounded-xl hover:bg-[#0052CC] transition-colors" data-id="element-1513">
              Still unsure? Contact us for guidance
            </a>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isBulkModalOpen} onClose={() => setIsBulkModalOpen(false)} title="Bulk pricing enquiry" data-id="element-1514">
        <form className="space-y-4" data-id="element-1515">
          <p className="text-sm text-slate-500 mb-4" data-id="element-1516">
            Looking for large quantities? Get in touch for our best trade rates.
          </p>
          <input type="text" placeholder="Name" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-1517" />
          <input type="email" placeholder="Email Address" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-1518" />
          <input type="tel" placeholder="Phone Number" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-1519" />
          <input type="number" placeholder="Estimated Quantity" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-1520" />
          <button className="w-full py-3 bg-[#0052CC] text-white font-bold rounded-xl hover:bg-[#0040A0]" data-id="element-1521">
            Request Trade Rates
          </button>
        </form>
      </Modal>
    </div>;
}